import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField,
} from '@mui/material';
import type { EventKind, PlanEvent } from '../../services/household-retirement-service';

export const EVENT_KIND_LABELS: Record<EventKind, string> = {
  vehicle_sale: 'Sell a vehicle',
  vehicle_purchase: 'Buy a vehicle (cash)',
  lump_sum_in: 'Money in (lump sum)',
  lump_sum_out: 'Money out (lump sum)',
  loan_payoff: 'Pay off a loan',
  custom: 'Other event',
};

export interface AccountChoice {
  id: string;
  name: string;
  kind: 'vehicle' | 'loan';
}

const blank = (): PlanEvent => ({ id: '', date: new Date().toISOString().slice(0, 7), kind: 'custom', label: '' });

/**
 * Add or edit a planned (or remembered) one-off event. Dates may be a month
 * ("2027-01") with an optional window for uncertain timing. Purchases are
 * modelled as cash.
 */
export const EventDialog: React.FC<{
  open: boolean;
  initial?: PlanEvent | null;
  accounts: AccountChoice[];
  onClose: () => void;
  onSave: (e: PlanEvent) => Promise<void>;
}> = ({ open, initial, accounts, onClose, onSave }) => {
  const [e, setE] = useState<PlanEvent>(initial ?? blank());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setE(initial ?? blank());
      setError(null);
    }
  }, [open, initial]);
  const set = (patch: Partial<PlanEvent>) => setE((x) => ({ ...x, ...patch }));
  const needsAccount = e.kind === 'vehicle_sale' || e.kind === 'loan_payoff';
  const choices = accounts.filter((a) => (e.kind === 'vehicle_sale' ? a.kind === 'vehicle' : a.kind === 'loan'));
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(e);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to save the event');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial?.id ? 'Edit event' : 'Add an event'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Kind" value={e.kind} onChange={(ev) => set({ kind: ev.target.value as EventKind })}
            inputProps={{ 'aria-label': 'Event kind' }}>
            {(Object.keys(EVENT_KIND_LABELS) as EventKind[]).map((k) => <MenuItem key={k} value={k}>{EVENT_KIND_LABELS[k]}</MenuItem>)}
          </TextField>
          <TextField label="What" value={e.label} onChange={(ev) => set({ label: ev.target.value })} fullWidth
            inputProps={{ 'aria-label': 'Event label' }} placeholder="e.g. Buy a Tesla Model 3" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="When (month)" type="month" value={e.date.slice(0, 7)} InputLabelProps={{ shrink: true }}
              onChange={(ev) => set({ date: ev.target.value })} inputProps={{ 'aria-label': 'Event month' }} />
            <TextField label="Window from" type="month" value={e.window_start ?? ''} InputLabelProps={{ shrink: true }}
              onChange={(ev) => set({ window_start: ev.target.value || undefined })} />
            <TextField label="to" type="month" value={e.window_end ?? ''} InputLabelProps={{ shrink: true }}
              onChange={(ev) => set({ window_end: ev.target.value || undefined })} />
          </Stack>
          {needsAccount && (
            <TextField select label={e.kind === 'vehicle_sale' ? 'Vehicle' : 'Loan'} value={e.account_id ?? ''}
              onChange={(ev) => set({ account_id: ev.target.value })} inputProps={{ 'aria-label': 'Event account' }}>
              {choices.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </TextField>
          )}
          {e.kind === 'vehicle_purchase' && (
            <TextField label="Vehicle" value={e.asset_name ?? ''} onChange={(ev) => set({ asset_name: ev.target.value, funding: 'cash' })} />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField label={e.kind === 'custom' ? 'Amount (+ in, − out)' : e.kind === 'vehicle_sale' ? 'Proceeds (blank = its value)' : 'Amount'}
              type="number" value={e.amount ?? ''} sx={{ minWidth: 240 }} inputProps={{ 'aria-label': 'Event amount' }}
              onChange={(ev) => set({ amount: ev.target.value === '' ? null : Number(ev.target.value) })} />
            <FormControlLabel label="Amount is an estimate" control={
              <Checkbox checked={!!e.amount_assumed} onChange={(ev) => set({ amount_assumed: ev.target.checked })} />} />
          </Stack>
          <TextField label="Notes" value={e.notes ?? ''} onChange={(ev) => set({ notes: ev.target.value || undefined })} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void save()} disabled={saving || !e.label.trim() || !e.date}>
          {saving ? 'Saving…' : 'Save event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
